import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { TransitionSingleBadge, SingleBadge } from '@ynap/badge';
import BEMHelper from '@ynap/bem-helper';
import { CombinedImage } from '@ynap/image';
import { withOptimizely } from '@ynap/optimizely-utils';
import { PriceWithSchema } from '@ynap/price';

const bem = new BEMHelper('ProductItem');

const ProductSummary = (props) => {
    const {
        children,
        className,
        country,
        cropDetails = true,
        CTABtn,
        detailedView = true,
        details,
        doubleImageModifier,
        doubleImageWithoutPadding,
        hasDotAlert = false,
        hidePrice = false,
        ImageComponent,
        imageOverlayElement,
        imageSrcsetSize,
        isWishlist = false,
        listingPage,
        loading,
        locale,
        messages,
        modifier,
        nameModifier,
        omnibusAlignment,
        optimizely,
        product,
        productImageAspectRatio,
        QuickAddButton,
        searchResults,
        type = '',
        withIntersectionObserver = true,
        withSkeleton = true,
        ...rest
    } = props;

    const { badge, consideredBadge, designerName = '', imgSrc, name = '', onSale, partNumber, price, saleExclusive } = product;
    const displayable = Object.prototype.hasOwnProperty.call(product, 'displayable') ? product.displayable : true; // Products with no 'displayable' property should default to true
    const productIsUnavailable = !displayable;
    const unavailableProduct = productIsUnavailable ? 'unavailableProduct' : '';
    const skeleton = loading ? 'skeleton' : null;
    const quickAdd = QuickAddButton ? 'quickAdd' : '';
    const modifiers = modifier ? { skeleton, unavailableProduct, [modifier]: true } : { skeleton, unavailableProduct };
    const imgAltString = `${designerName || ''}${name ? ` ${name}` : ''}`;

    return (
        <div className={bem(null, null, className)} {...rest}>
            <div className={bem('p', [type, modifier])}>
                <meta content={partNumber} itemProp="productID" /> {/* product info for personal shopper chrome extension */}
                <div className={bem('imageContainer', [!displayable ? 'imgModifier' : '', type, quickAdd, modifier])}>
                    <div
                        onClick={() => {
                            (QuickAddButton && optimizely?.track('recommendation_image_click')) || optimizely?.track('recommendations_pids_ctr');
                        }}
                    >
                        <CombinedImage
                            hideSecondaryOnMobile
                            ImageComponent={ImageComponent}
                            alt={imgAltString}
                            aspectRatio={productImageAspectRatio}
                            displayError={!(loading || imgSrc?.primary)}
                            doubleImageModifier={doubleImageModifier}
                            doubleImageWithoutPadding={doubleImageWithoutPadding}
                            imageOverlayElement={imageOverlayElement}
                            imageSrcsetSize={imageSrcsetSize}
                            loading={loading}
                            primaryPlpSrcset={imgSrc?.primaryPlpSrcset}
                            primarySrcset={imgSrc?.primarySrcset}
                            secondaryPlpSrcset={imgSrc?.secondaryPlpSrcset}
                            secondarySrc={QuickAddButton ? undefined : imgSrc?.secondary}
                            secondarySrcset={imgSrc?.secondarySrcset}
                            src={imgSrc?.primary}
                            withIntersectionObserver={withIntersectionObserver}
                            withSkeleton={withSkeleton}
                            yOffset="0%"
                        />
                    </div>
                    {QuickAddButton && <QuickAddButton />}
                </div>
                <div className={bem('skeletonContainer', [skeleton, unavailableProduct, modifier, type])}>
                    {CTABtn}

                    <div className={bem('content', { ...modifiers, descriptionWrapped: modifier === 'wiwImageInfoStyle' && !badge })}>
                        {loading ? null : (
                            <>
                                {designerName && (
                                    <span className={bem('details', [cropDetails ? 'brief' : '', modifier])} data-product-id={partNumber}>
                                        <span className={bem('designer', modifier)} data-testid="pid-summary-designer" dir="ltr">
                                            {designerName}
                                        </span>
                                        {/* Line break added to make -line-clamp behave */}
                                        {name && (
                                            <>
                                                <span className={bem('lineBreakHack', modifier)}>
                                                    <br />
                                                </span>
                                                <span className={bem('name', nameModifier)} data-testid="pid-summary-description">
                                                    {name}
                                                </span>
                                            </>
                                        )}
                                    </span>
                                )}

                                {children}

                                {productIsUnavailable && <div className={bem('message')}>{messages.product.unavailableProduct()}</div>}

                                {displayable && (
                                    <>
                                        {detailedView && !hidePrice && (
                                            <PriceWithSchema
                                                {...price}
                                                badgeKey={product?.badge?.key}
                                                className={bem('price', modifier)}
                                                country={country}
                                                designerName={product?.designerName}
                                                details={details}
                                                isWishlist={isWishlist}
                                                listingPage={listingPage}
                                                locale={locale}
                                                messages={messages}
                                                omnibusAlignment={omnibusAlignment}
                                                priceSuffix={messages.price?.taxIncluded?.()}
                                                saleExclusive={saleExclusive}
                                                searchResults={searchResults}
                                            />
                                        )}
                                        {detailedView && (
                                            <TransitionSingleBadge badge={badge} className={bem('badge', modifier)} hasDotAlert={hasDotAlert} onSale={onSale} />
                                        )}
                                        {consideredBadge && (
                                            <>
                                                <SingleBadge
                                                    considered
                                                    badge={{ label: consideredBadge, key: 'CONSIDERED_BADGE' }}
                                                    className={bem('consideredBadge')}
                                                    translation={messages.considered.badge()}
                                                />
                                            </>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

ProductSummary.propTypes = {
    product: PropTypes.object.isRequired,
    price: PropTypes.object,
    messages: PropTypes.shape({
        product: PropTypes.shape({
            onSale: PropTypes.func.isRequired,
            unavailableProduct: PropTypes.func.isRequired,
        }).isRequired,
        price: PropTypes.shape({
            taxIncluded: PropTypes.func,
        }),
        considered: PropTypes.shape({
            badge: PropTypes.func.isRequired,
        }),
    }).isRequired,
    modifier: PropTypes.string,
    nameModifier: PropTypes.string,
    loading: PropTypes.bool,
    className: PropTypes.string,
    children: PropTypes.node,
    detailedView: PropTypes.bool,
    cropDetails: PropTypes.bool,
    productImageAspectRatio: PropTypes.string,
    imageSrcsetSize: PropTypes.string,
    withIntersectionObserver: PropTypes.bool,
    hasDotAlert: PropTypes.bool,
    locale: PropTypes.string.isRequired,
    country: PropTypes.string.isRequired,
    withSkeleton: PropTypes.bool,
    hidePrice: PropTypes.bool,
    type: PropTypes.string,
    ImageComponent: PropTypes.elementType,
    QuickAddButton: PropTypes.elementType,
    optimizely: PropTypes.shape({
        track: PropTypes.func.isRequired,
    }),
    doubleImageWithoutPadding: PropTypes.string,
    doubleImageModifier: PropTypes.string,
    details: PropTypes.bool,
    omnibusAlignment: PropTypes.bool,
    searchResults: PropTypes.bool,
    listingPage: PropTypes.bool,
    isWishlist: PropTypes.bool,
    CTABtn: PropTypes.elementType,
    recommendedProduct: PropTypes.object,
    imageOverlayElement: PropTypes.node,
};

export const areEqual = (prevProps, nextProps) => {
    if (prevProps.product.partNumber !== nextProps.product.partNumber) {
        return false;
    }

    if (prevProps.loading !== nextProps.loading || prevProps.QuickAddButton !== nextProps.QuickAddButton) {
        return false;
    }

    if (prevProps.CTABtn !== nextProps.CTABtn) {
        return false;
    }

    // badges might update when user data is fetched and segmented badges are shown
    if (prevProps.product.badge?.key !== nextProps.product.badge?.key) {
        return false;
    }

    // update image on hover over a colour
    if (prevProps.colourImagePartNumber !== nextProps.colourImagePartNumber) {
        return false;
    }

    if (prevProps.imageOverlayElement !== nextProps.imageOverlayElement) {
        return false;
    }

    return true;
};

export default memo(withOptimizely(ProductSummary), areEqual);
